/*
 * Tenue — product-form.js
 * Add to cart, gift card recipient fields and pickup availability.
 */
(function () {
  'use strict';

  const Tenue = window.Tenue;

  /* ------------------------------------------------------------------------
     <product-form>: adds to cart without leaving the page.
     ------------------------------------------------------------------------ */
  if (!customElements.get('product-form')) {
    class ProductForm extends HTMLElement {
      connectedCallback() {
        this.form = document.getElementById(`ProductForm-${this.dataset.sectionId}`);
        if (!this.form) return;
        this.submitButton = this.form.querySelector('button[name="add"]');
        this.errorWrapper = this.querySelector('[data-form-error]');
        this.errorMessage = this.querySelector('[data-form-error-message]');
        this.onSubmit = (event) => this.handleSubmit(event);
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      }

      handleSubmit(event) {
        if (this.hasAttribute('data-skip-cart')) return;
        if (!window.theme || window.theme.cartType !== 'notification') return;
        if (!document.querySelector('cart-notification')) return;

        event.preventDefault();
        if (!this.submitButton || this.submitButton.disabled) return;
        if (this.submitButton.getAttribute('aria-busy') === 'true') return;
        this.addToCart();
      }

      async addToCart() {
        this.clearError();
        this.submitButton.setAttribute('aria-busy', 'true');

        const formData = new FormData(this.form);
        formData.append('sections', 'cart-icon-bubble');
        formData.append('sections_url', window.location.pathname);

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity'), 10) || 1;
        const deferred = this.dispatchLinesUpdate(variantId, quantity);

        try {
          const response = await fetch(window.theme.routes.cartAdd, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body: formData,
          });
          const data = await response.json();

          if (data.status) {
            const message = data.description || data.message || window.theme.strings.cartError;
            this.showError(message);
            this.resolveLinesUpdate(deferred, [{ code: 'INVALID', field: ['lines'], message }]);
            return;
          }

          this.resolveLinesUpdate(deferred, []);
          Tenue.emit('cart:added', data);
        } catch (error) {
          this.showError(window.theme.strings.cartError);
          const events = window.StandardEvents || {};
          if (events.CartErrorEvent) {
            this.dispatchEvent(
              new events.CartErrorEvent({ error: error.message || 'Network error', code: 'SERVICE_UNAVAILABLE' })
            );
          }
          if (deferred) deferred.reject(error);
        } finally {
          this.submitButton.removeAttribute('aria-busy');
        }
      }

      dispatchLinesUpdate(variantId, quantity) {
        const events = window.StandardEvents || {};
        if (!events.CartLinesUpdateEvent || !variantId) return null;
        const deferred = events.CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new events.CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: String(variantId), quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      async resolveLinesUpdate(deferred, userErrors) {
        if (!deferred) return;
        const events = window.StandardEvents || {};
        try {
          const response = await fetch(`${window.theme.routes.cart}.js`, { headers: { Accept: 'application/json' } });
          const cart = await response.json();
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            userErrors,
            warnings: [],
          });
        } catch (error) {
          deferred.reject(error);
        }
      }

      showError(message) {
        if (!this.errorWrapper || !this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorWrapper.hidden = false;
      }

      clearError() {
        if (this.errorWrapper) this.errorWrapper.hidden = true;
      }
    }
    customElements.define('product-form', ProductForm);
  }

  /* ------------------------------------------------------------------------
     <recipient-form>: gift card recipient fields.
     ------------------------------------------------------------------------ */
  if (!customElements.get('recipient-form')) {
    class RecipientForm extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('[data-recipient-checkbox]');
        this.control = this.querySelector('[data-recipient-control]');
        this.offset = this.querySelector('[data-recipient-offset]');
        this.fields = this.querySelector('[data-recipient-fields]');
        this.inputs = Array.from(this.querySelectorAll('[data-recipient-input]'));
        if (!this.checkbox || !this.fields) return;

        this.checkbox.disabled = false;
        if (this.control) this.control.disabled = true;
        if (this.offset) this.offset.value = String(new Date().getTimezoneOffset());
        this.checkbox.addEventListener('change', () => this.sync());
        this.sync();
      }

      sync() {
        const enabled = this.checkbox.checked;
        this.fields.hidden = !enabled;
        this.inputs.forEach((input) => {
          input.disabled = !enabled;
          if (!enabled) input.value = '';
        });
        if (this.offset) this.offset.disabled = !enabled;
      }
    }
    customElements.define('recipient-form', RecipientForm);
  }

  /* ------------------------------------------------------------------------
     <pickup-availability>
     ------------------------------------------------------------------------ */
  if (!customElements.get('pickup-availability')) {
    class PickupAvailability extends HTMLElement {
      connectedCallback() {
        if (this.hasAttribute('data-available')) this.load(this.dataset.variantId);
      }

      update(variant) {
        if (variant && variant.available) {
          this.load(variant.id);
        } else {
          this.innerHTML = '';
        }
      }

      async load(variantId) {
        if (!variantId) return;
        let rootUrl = this.dataset.rootUrl || '/';
        if (!rootUrl.endsWith('/')) rootUrl += '/';
        try {
          const response = await fetch(`${rootUrl}variants/${variantId}/?section_id=pickup-availability`);
          const doc = Tenue.parseHTML(await response.text());
          const section = doc.querySelector('.shopify-section');
          this.innerHTML = section ? section.innerHTML : '';
          const opener = this.querySelector('[data-pickup-open]');
          const dialog = this.querySelector('[data-pickup-dialog]');
          if (opener && dialog) opener.addEventListener('click', () => dialog.showModal());
        } catch (error) {
          this.innerHTML = '';
        }
      }
    }
    customElements.define('pickup-availability', PickupAvailability);
  }
})();
